import type { RequestOptions } from '@@/plugin-request/request';
import { request as umiRequest } from '@umijs/max';
import { BizAppConfig } from 'config/application.manifest';
import { ensureValidAccessToken, getAccessToken } from '@/services/oauth2';

type ScopedRequestOptions = RequestOptions;

const AUTH_ENDPOINTS_SKIP_AUTH_HEADER: string[] = [];
const AUTH_ENDPOINTS_SKIP_TENANT_HEADER = [
  '/bff/iam-ui/members/me',
  '/bff/auth/switch-tenant',
  '/bff/auth/tenant-list',
];
const CURRENT_TENANT_UUID_STORAGE_KEY = 'currentTenantUuid';

export const resolveBaseURL = (baseURL?: string) => {
  const explicit = String(baseURL ?? BizAppConfig.appApiUrl ?? '').trim();
  if (explicit) {
    return explicit;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:3000';
    }
  }
  return '';
};

const getRequestPathname = (url?: string) => {
  if (!url) return '';

  try {
    const base =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost';
    return new URL(url, base).pathname;
  } catch {
    return url;
  }
};

const shouldSkipAuthHeader = (url?: string) => {
  const pathname = getRequestPathname(url);
  return AUTH_ENDPOINTS_SKIP_AUTH_HEADER.some((endpoint) =>
    pathname.includes(endpoint),
  );
};

const shouldSkipTenantHeader = (url?: string) => {
  const pathname = getRequestPathname(url);
  return AUTH_ENDPOINTS_SKIP_TENANT_HEADER.some((endpoint) =>
    pathname.includes(endpoint),
  );
};

const getTenantUuidFromStorage = () => {
  try {
    return localStorage.getItem(CURRENT_TENANT_UUID_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
};

const getOrganizationUuidFromStorage = () => {
  try {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson) as API.CurrentUser;
      return (
        user.organizationUuid || user.lastLoginOrganizationUuid || undefined
      );
    }
  } catch {
    // ignore
  }
  return undefined;
};

export const createRequest = (baseURL?: string) => {
  const scopedBaseURL = resolveBaseURL(baseURL);
  return async <T = unknown>(url: string, options?: ScopedRequestOptions) => {
    const nextOptions: ScopedRequestOptions = {
      ...(options || {}),
    };

    if (!shouldSkipAuthHeader(url)) {
      const token =
        (await ensureValidAccessToken()) || getAccessToken() || undefined;
      const skipTenant = shouldSkipTenantHeader(url);
      const tenantUuid = skipTenant ? undefined : getTenantUuidFromStorage();
      const organizationUuid = skipTenant
        ? undefined
        : getOrganizationUuidFromStorage();

      if (token || tenantUuid || organizationUuid) {
        nextOptions.headers = {
          ...nextOptions.headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(tenantUuid ? { 'x-tenant-id': tenantUuid } : {}),
          ...(organizationUuid
            ? { 'x-organization-id': organizationUuid }
            : {}),
        };
      }
    }

    return (
      umiRequest as unknown as <R = unknown>(
        requestUrl: string,
        requestOptions?: ScopedRequestOptions,
      ) => Promise<R>
    )<T>(url, {
      baseURL: scopedBaseURL,
      ...nextOptions,
    });
  };
};

export const createAppRequest = () => createRequest(BizAppConfig.appApiUrl);
